<script setup>
/**
 * People → who is waiting to come in, above the room (prototype People, cd112:
 * "Waiting sits ABOVE the room, because it is the only part of this list with
 * an action attached"). Each person stays until admitted, declined or gone;
 * the room publishes the list and its own ways in (sessionStore), so the tab
 * admits exactly as the room's banner does.
 *
 *   WAITING TO COME IN 3                  Admit all
 *   (GB) Grace Bell   waiting 6 min       [Admit] [✕]
 *   IN THIS SESSION 4
 *
 * On a phone it is the mobile room's (prototype sheetPeople): "Waiting to join
 * · 3", and each row's Deny and Admit side by side. Admit all is on the stage's
 * banner there.
 */
import { computed, onBeforeUnmount, ref } from 'vue';
import { useSessionStore } from '../../stores/sessionStore';
import XlAvatar from '../xl/XlAvatar.vue';

const props = defineProps({
  // The room's rows below this group, for its "In this session" count.
  inSessionCount: { type: Number, default: 0 },
  // A webinar draws its own On stage / Audience headings under this group.
  roomHeading: { type: Boolean, default: true },
  // The room is on a phone.
  phone: { type: Boolean, default: false },
});

const sessionStore = useSessionStore();
const waiting = computed(() => sessionStore.waitingAttendees ?? []);
const actions = computed(() => sessionStore.waitingActions);

const now = ref(Date.now());
const clock = setInterval(() => { now.value = Date.now(); }, 30_000);
onBeforeUnmount(() => clearInterval(clock));

const waitedFor = (since) => {
  const minutes = Math.floor((now.value - (since ?? now.value)) / 60_000);
  return minutes < 1 ? 'just now' : `waiting ${minutes} min`;
};
</script>

<template>
  <div class="ppl-wait">
    <div class="ppl-grp ppl-grp--knock" data-testid="people-waiting-heading">
      {{ props.phone ? 'Waiting to join' : 'Waiting to come in' }} <span>{{ waiting.length }}</span>
      <button
          v-if="!props.phone"
          type="button"
          class="ppl-admit-all"
          data-testid="people-admit-all"
          :disabled="sessionStore.isAdmittingAll"
          @click="actions?.admitAll()"
      >Admit all</button>
    </div>

    <div class="ppl-rows" data-testid="people-waiting">
      <div v-for="w in waiting" :key="w.participantIdentity" class="ppl-row" data-testid="people-waiting-row">
        <XlAvatar :name="w.participantName" :src="w.avatar || ''" :size="32" />
        <div class="ppl-row__t">
          <b>{{ w.participantName }}</b>
          <span>{{ waitedFor(w.since) }}</span>
        </div>
        <template v-if="props.phone">
          <button
              type="button"
              class="ppl-deny"
              :data-testid="`people-decline-${w.participantIdentity}`"
              @click="actions?.decline(w.participantIdentity)"
          >Deny</button>
          <button
              type="button"
              class="ppl-admit"
              :data-testid="`people-admit-${w.participantIdentity}`"
              @click="actions?.admit(w.participantIdentity)"
          >Admit</button>
        </template>
        <button
            v-if="!props.phone"
            type="button"
            class="ppl-admit"
            :data-testid="`people-admit-${w.participantIdentity}`"
            @click="actions?.admit(w.participantIdentity)"
        >Admit</button>
        <button
            v-if="!props.phone"
            type="button"
            class="ppl-decline"
            :title="`Don't let ${w.participantName} in`"
            :aria-label="`Don't let ${w.participantName} in`"
            :data-testid="`people-decline-${w.participantIdentity}`"
            @click="actions?.decline(w.participantIdentity)"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" aria-hidden="true"><path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8" /></svg>
        </button>
      </div>
    </div>

    <div v-if="props.roomHeading" class="ppl-grp" data-testid="people-in-session-heading">In this session <span>{{ props.inSessionCount }}</span></div>
  </div>
</template>

<style scoped>
/* The prototype's .rmgrp, .rmgrp.knock and .rmrow. Doubled class: the panel's
   older chat.scss styles buttons and divs deep inside the people list. */
.ppl-wait.ppl-wait { font-family: var(--xl-font-family); }

.ppl-wait .ppl-grp {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--xl-color-outline-variant, #B6AEBC);
}

.ppl-wait .ppl-grp span { font-size: 11px; font-weight: 700; color: var(--xl-color-outline, #867D8D); }

.ppl-wait .ppl-grp--knock { padding-top: 12px; color: var(--xl-color-warning, #8A5200); }
.ppl-wait .ppl-grp--knock span { color: var(--xl-color-warning, #8A5200); }

/* "Admit all" is a quiet button: it is a list, not an alarm (cd125). */
.ppl-wait .ppl-admit-all {
  margin-left: auto;
  height: 26px;
  padding: 0 11px;
  border: 1px solid var(--xl-color-border, #EDE7F0);
  border-radius: 99px;
  background: #fff;
  color: var(--xl-color-on-surface-variant, #514A57);
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  cursor: pointer;
}

.ppl-wait .ppl-admit-all:hover:not(:disabled) { border-color: var(--xl-color-surface-tint, #8154A6); color: var(--xl-color-primary, #5E2F78); }
.ppl-wait .ppl-admit-all:disabled { opacity: 0.5; cursor: default; }

.ppl-wait .ppl-rows { padding: 4px 14px; }

.ppl-wait .ppl-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid var(--xl-color-border-subtle, #F6F2F8);
}

.ppl-wait .ppl-row:last-child { border-bottom: none; }

.ppl-wait .ppl-row__t { flex: 1; min-width: 0; }
.ppl-wait .ppl-row__t b {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--xl-color-on-surface, #1C1720);
}
.ppl-wait .ppl-row__t span { display: block; font-size: 11.5px; color: var(--xl-color-outline, #867D8D); }

/* Admit: the primary button at row size (.rmppl .rmrow .btn.sm). */
.ppl-wait .ppl-admit {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 13px;
  border: 0;
  border-radius: 99px;
  background: linear-gradient(155deg, var(--xl-color-surface-tint, #8154A6), var(--xl-color-primary, #5E2F78));
  color: #fff;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 5px 16px rgba(94, 47, 120, 0.26);
  cursor: pointer;
}

/* Not letting them in: the outlined red ✕ (.ib, color --cr). */
.ppl-wait .ppl-decline {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--xl-color-border, #EDE7F0);
  border-radius: 11px;
  background: #fff;
  color: var(--xl-color-error, #A32020);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.ppl-wait .ppl-deny {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 13px;
  border: 1px solid #E2DBE6;
  border-radius: 99px;
  background: #fff;
  color: var(--xl-color-on-surface, #1C1720);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.ppl-wait .ppl-deny:focus-visible { outline: 2px solid var(--xl-color-primary, #5E2F78); outline-offset: 2px; }

.ppl-wait .ppl-decline:hover { background: var(--xl-color-error-container, #FCEBEB); }

.ppl-wait .ppl-admit:focus-visible,
.ppl-wait .ppl-decline:focus-visible,
.ppl-wait .ppl-admit-all:focus-visible { outline: 2px solid var(--xl-color-primary, #5E2F78); outline-offset: 2px; }
</style>
